import React from 'react';
import {
    AppBar,
    Toolbar,
    Typography,
    Button,
    Box,
    Tooltip,
    Avatar,
    IconButton,
    Menu,
    MenuItem,
    Drawer,
    List,
    ListItemButton,
    ListItemText,
    Divider,
    BottomNavigation,
    BottomNavigationAction,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import BusinessIcon from '@mui/icons-material/Business';
import DashboardIcon from '@mui/icons-material/Dashboard';
import StorefrontIcon from '@mui/icons-material/Storefront';
import DescriptionIcon from '@mui/icons-material/Description';
import ChecklistIcon from '@mui/icons-material/Checklist';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PeopleIcon from '@mui/icons-material/People';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { useNavigate, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSelection } from '../../context/SelectionContext';
import logoLarge from '../../mfs-logo.png';
import logoSmall from '../../mfs-logo-small.png';
import { auditsEnabled, reportsEnabled } from '../../config/featureFlags';

const baseNavLinks = [
    { to: '/dashboard', label: 'Dashboard', icon: DashboardIcon },
    { to: '/branches', label: 'Branches', icon: StorefrontIcon },
    { to: '/documents', label: 'Documents', icon: DescriptionIcon },
    { to: '/audits', label: 'Audits', icon: ChecklistIcon },
    { to: '/reports', label: 'Reports', icon: AssessmentIcon },
    { to: '/contacts', label: 'Contact Personnel', icon: PeopleIcon },
];

const navLinks = baseNavLinks.filter(link => {
    if (link.to === '/audits' && !auditsEnabled) return false;
    if (link.to === '/reports' && !reportsEnabled) return false;
    return true;
});

const NavButton = ({ to, label, themeMode }) => (
    <NavLink to={to} end={to === '/dashboard'} style={{ textDecoration: 'none' }}>
        {({ isActive }) => (
            <Button
                color="inherit"
                sx={{
                    borderRadius: 0,
                    position: 'relative',
                    fontWeight: isActive ? 600 : 400,
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        bottom: 0,
                        width: '100%',
                        height: 3,
                        bgcolor: isActive ? 'primary.main' : 'transparent',
                        transition: 'background-color 0.2s ease',
                    },
                    color: themeMode === 'dark' ? 'common.white' : 'primary.main',
                    '&:hover': {
                        color: themeMode === 'dark' ? 'common.white' : 'primary.dark',
                    },
                }}
            >
                {label}
            </Button>
        )}
    </NavLink>
);

const Navbar = ({ themeMode, onToggleTheme }) => {
    // Get isAdmin status and the logout function from our Auth Context
    const { isAdmin, logout, userRole, user, userBranchIds } = useAuth();
    const { selectedBusinessName } = useSelection();
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);
    const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = React.useState(null);
    const mobileMoreOpen = Boolean(mobileMoreAnchorEl);
    const [drawerOpen, setDrawerOpen] = React.useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const location = useLocation();

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleMobileMoreOpen = (event) => {
        setMobileMoreAnchorEl(event.currentTarget);
    };

    const handleMobileMoreClose = () => {
        setMobileMoreAnchorEl(null);
    };

    const toggleDrawer = (nextOpen) => () => {
        setDrawerOpen(nextOpen);
    };

    const handleLogout = async () => {
        try {
            await logout();
            // Redirect to landing page after logout is complete
            navigate('/', { replace: true });
        } catch (error) {
            console.error('Failed to log out', error);
        }
    };

    const displayName = user?.displayName || user?.email?.split('@')[0] || 'Unknown User';
    const userEmail = user?.email;

    const availableLinks = React.useMemo(() => {
        if (userRole === 'Supervisor') {
            return navLinks.filter(link => link.to !== '/branches');
        }
        if (userRole === 'Employee') {
            return navLinks.filter(link => {
                if (link.to === '/documents' || link.to === '/audits') return false;
                if (link.to === '/branches') return userBranchIds.length > 1;
                return true;
            });
        }
        return navLinks;
    }, [userRole, userBranchIds]);

    const mobilePrimaryLinks = React.useMemo(() => {
        if (isAdmin) {
            return [
                { to: '/dashboard', label: 'Home', icon: DashboardIcon },
                { to: '/branches', label: 'Branches', icon: StorefrontIcon },
                ...(auditsEnabled ? [{ to: '/audits', label: 'Audits', icon: ChecklistIcon }] : []),
                ...(reportsEnabled ? [{ to: '/reports', label: 'Reports', icon: AssessmentIcon }] : []),
            ];
        }

        if (userRole === 'Employee') {
            return [
                { to: '/dashboard', label: 'Home', icon: DashboardIcon },
                ...(userBranchIds.length > 1 ? [{ to: '/branches', label: 'Branches', icon: StorefrontIcon }] : []),
                ...(reportsEnabled ? [{ to: '/reports', label: 'Reports', icon: AssessmentIcon }] : []),
                { to: '/contacts', label: 'Contacts', icon: PeopleIcon },
            ];
        }

        return [
            { to: '/dashboard', label: 'Home', icon: DashboardIcon },
            ...(userRole === 'Supervisor' ? [] : [{ to: '/branches', label: 'Branches', icon: StorefrontIcon }]),
            { to: '/documents', label: 'Docs', icon: DescriptionIcon },
            ...(auditsEnabled ? [{ to: '/audits', label: 'Audits', icon: ChecklistIcon }] : []),
            ...(reportsEnabled ? [{ to: '/reports', label: 'Reports', icon: AssessmentIcon }] : []),
            { to: '/contacts', label: 'Contacts', icon: PeopleIcon },
        ];
    }, [isAdmin, userRole, userBranchIds]);

    const mobileMoreLinks = React.useMemo(() => {
        if (!isAdmin) return [];
        return [
            { to: '/businesses', label: 'Business', icon: BusinessIcon },
            { to: '/documents', label: 'Documents', icon: DescriptionIcon },
            { to: '/contacts', label: 'Contacts', icon: PeopleIcon },
        ];
    }, [isAdmin]);

    const showBusinessBadge = Boolean(selectedBusinessName)
        && (userRole === 'Supervisor' || userRole === 'Business Owner');

    const renderNavLinks = (orientation = 'horizontal') => (
        <Box
            sx={{
                display: orientation === 'horizontal' ? { xs: 'none', sm: 'flex' } : 'flex',
                flexDirection: orientation === 'horizontal' ? 'row' : 'column',
                gap: orientation === 'horizontal' ? 1 : 0,
                mr: orientation === 'horizontal' ? 2 : 0,
                width: orientation === 'horizontal' ? 'auto' : '100%',
            }}
        >
            {isAdmin && (
                orientation === 'horizontal' ? (
                    <NavButton to="/businesses" label="Businesses" themeMode={themeMode} />
                ) : (
                    <ListItemButton
                        selected={location.pathname === '/businesses' || location.pathname.startsWith('/businesses/')}
                        onClick={() => {
                            navigate('/businesses');
                            setDrawerOpen(false);
                        }}
                    >
                        <BusinessIcon sx={{ mr: 2 }} fontSize="small" />
                        <ListItemText primary="Businesses" />
                    </ListItemButton>
                )
            )}
            {availableLinks.map(link => {
                const Icon = link.icon;
                if (orientation === 'horizontal') {
                    return <NavButton key={link.to} to={link.to} label={link.label} themeMode={themeMode} />;
                }
                const dashNormalized = location.pathname === '/dashboard' ? '/dashboard' : location.pathname;
                const isSelected = dashNormalized === link.to || dashNormalized.startsWith(`${link.to}/`);
                return (
                    <ListItemButton
                        key={link.to}
                        selected={isSelected}
                        onClick={() => {
                            navigate(link.to);
                            setDrawerOpen(false);
                        }}
                    >
                        <Icon sx={{ mr: 2 }} fontSize="small" />
                        <ListItemText primary={link.label} />
                    </ListItemButton>
                );
            })}
        </Box>
    );

    const bottomNavValue = React.useMemo(() => {
        const mobileMatch = mobilePrimaryLinks.find(link => location.pathname.startsWith(link.to));
        if (mobileMatch) return mobileMatch.to;
        if (mobileMoreLinks.some(link => location.pathname.startsWith(link.to))) {
            return 'more';
        }
        const sorted = [...availableLinks, ...(isAdmin ? [{ to: '/businesses' }] : [])].sort((a, b) => b.to.length - a.to.length);
        const match = sorted.find(link => location.pathname.startsWith(link.to));
        if (match) return match.to;
        return null;
    }, [location.pathname, availableLinks, isAdmin, mobilePrimaryLinks, mobileMoreLinks]);

    return (
        <>
            <AppBar
                position="fixed"
                color="background"
                elevation={isMobile ? 0 : 1}
                sx={{
                    borderBottom: isMobile ? 'none' : `1px solid ${theme.palette.divider}`,
                    backgroundColor: themeMode === 'dark'
                        ? theme.palette.background.paper
                        : theme.palette.common.white,
                    backgroundImage: themeMode === 'dark' ? undefined : 'none',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: theme.zIndex.appBar,
                    transform: 'translateZ(0)',
                    paddingTop: 'env(safe-area-inset-top)',
                }}
            >
                <Toolbar sx={{ px: isMobile ? 2 : 4 }}>
                    {isMobile ? (
                        <IconButton edge="start" onClick={toggleDrawer(true)}>
                            <MenuIcon />
                        </IconButton>
                    ) : null}
                    <Box
                        sx={{
                            flexGrow: 1,
                            ml: isMobile ? 1 : 0,
                            display: 'flex',
                            alignItems: 'center',
                            minWidth: 0,
                            gap: isMobile ? 0.75 : 1.5,
                        }}
                    >
                        <Box
                            component="img"
                            src={isMobile ? logoSmall : logoLarge}
                            alt="Mister Food Safety"
                            sx={{
                                height: isMobile ? 28 : 40,
                                width: 'auto',
                                objectFit: 'contain',
                                filter: themeMode === 'dark' ? 'brightness(0) invert(1)' : 'none',
                                transition: 'filter 0.2s ease',
                            }}
                        />
                        {showBusinessBadge ? (
                            <Box
                                sx={(theme) => ({
                                    maxWidth: { xs: 'min(80vw, 280px)', sm: 240 },
                                    px: { xs: 1.5, sm: 2.5 },
                                    py: { xs: 0.45, sm: 0.7 },
                                    borderRadius: 3,
                                    background: theme.palette.mode === 'dark'
                                        ? 'linear-gradient(120deg, rgba(129,140,248,0.32) 0%, rgba(59,130,246,0.2) 100%)'
                                        : 'linear-gradient(120deg, rgba(99,102,241,0.18) 0%, rgba(37,99,235,0.12) 100%)',
                                    border: '1px solid rgba(129,140,248,0.35)',
                                    boxShadow: theme.palette.mode === 'dark' ? 'none' : theme.shadows[2],
                                    overflow: 'hidden',
                                    flexShrink: 1,
                                })}
                            >
                                <Typography
                                    variant={isMobile ? 'caption' : 'subtitle2'}
                                    sx={(theme) => ({
                                        fontWeight: 700,
                                        letterSpacing: 0.8,
                                        textTransform: 'uppercase',
                                        fontFamily: `'Poppins', ${theme.typography.fontFamily}`,
                                        color: theme.palette.mode === 'dark' ? '#eef2ff' : '#312e81',
                                        overflow: 'hidden',
                                        whiteSpace: 'normal',
                                        lineHeight: 1.2,
                                        textAlign: 'center',
                                        wordBreak: 'break-word',
                                    })}
                                >
                                    {selectedBusinessName}
                                </Typography>
                            </Box>
                        ) : null}
                    </Box>

                    {!isMobile && renderNavLinks('horizontal')}

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {user && (
                            <>
                                <Tooltip title="Account">
                                    <IconButton onClick={handleMenuOpen} size="small">
                                        <Avatar sx={{ width: 32, height: 32 }}>
                                            {displayName.charAt(0).toUpperCase()}
                                        </Avatar>
                                    </IconButton>
                                </Tooltip>
                                <Menu
                                    anchorEl={anchorEl}
                                    open={open}
                                    onClose={handleMenuClose}
                                    onClick={handleMenuClose}
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                >
                                    <MenuItem disabled>
                                        <Box>
                                            <Typography variant="subtitle2">{displayName}</Typography>
                                            {userEmail && (
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    {userEmail}
                                                </Typography>
                                            )}
                                            {userRole && (
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Role: {userRole}
                                                </Typography>
                                            )}
                                        </Box>
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => {
                                            onToggleTheme();
                                        }}
                                    >
                                        {themeMode === 'dark' ? (
                                            <>
                                                <Brightness7Icon fontSize="small" sx={{ mr: 1 }} />
                                                Switch to Light Mode
                                            </>
                                        ) : (
                                            <>
                                                <Brightness4Icon fontSize="small" sx={{ mr: 1 }} />
                                                Switch to Dark Mode
                                            </>
                                        )}
                                    </MenuItem>
                                    <MenuItem onClick={() => navigate('/change-password')}>
                                        Change Password
                                    </MenuItem>
                                    <MenuItem onClick={handleLogout}>
                                        <LogoutIcon fontSize="small" sx={{ mr: 1 }} /> Logout
                                    </MenuItem>
                                </Menu>
                            </>
                        )}
                    </Box>
                </Toolbar>
            </AppBar>

            <Toolbar />

            <Drawer anchor="left" open={drawerOpen} onClose={toggleDrawer(false)}>
                <Box sx={{ width: 260, p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>Navigate</Typography>
                    <Divider sx={{ mb: 1 }} />
                    <List>{renderNavLinks('vertical')}</List>
                    <Divider sx={{ mt: 'auto', mb: 1 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Theme</Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={onToggleTheme}
                            startIcon={themeMode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
                        >
                            {themeMode === 'dark' ? 'Light mode' : 'Dark mode'}
                        </Button>
                    </Box>
                </Box>
            </Drawer>

            {isMobile && (
                <>
                    <BottomNavigation
                        showLabels
                        value={bottomNavValue}
                        onChange={(event, newValue) => {
                            if (!newValue) return;
                            if (newValue === 'more') {
                                handleMobileMoreOpen(event);
                                return;
                            }
                            navigate(newValue);
                        }}
                        sx={{
                            position: 'fixed',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            borderTop: `1px solid ${theme.palette.divider}`,
                            zIndex: theme.zIndex.appBar,
                            transform: 'translateZ(0)',
                            bgcolor: theme.palette.background.paper,
                            backdropFilter: 'blur(6px)',
                            paddingBottom: 'env(safe-area-inset-bottom)',
                            height: 'auto',
                        }}
                    >
                        {mobilePrimaryLinks.map(link => (
                            <BottomNavigationAction
                                key={link.to}
                                label={link.label}
                                value={link.to}
                                icon={<link.icon />}
                            />
                        ))}
                        {mobileMoreLinks.length > 0 && (
                            <BottomNavigationAction
                                label="More"
                                value="more"
                                icon={<MoreHorizIcon />}
                            />
                        )}
                    </BottomNavigation>
                    <Menu
                        anchorEl={mobileMoreAnchorEl}
                        open={mobileMoreOpen}
                        onClose={handleMobileMoreClose}
                        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    >
                        {mobileMoreLinks.map(link => {
                            const Icon = link.icon;
                            return (
                                <MenuItem
                                    key={link.to}
                                    onClick={() => {
                                        handleMobileMoreClose();
                                        navigate(link.to);
                                    }}
                                >
                                    <Icon fontSize="small" style={{ marginRight: 8 }} />
                                    {link.label}
                                </MenuItem>
                            );
                        })}
                    </Menu>
                </>
            )}
        </>
    );
};

export default Navbar;
